import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
	useGetPendingRequests,
	postApprovePendingRequest,
	postRejectPendingRequest,
} from "../../../../utils/api/functions/sitepages";
import fallbackAvatar from "../../../../assets/no_profile_pic.png";

// Lightweight debounce hook to soften search typing
function useDebouncedValue(value, delay = 300) {
	const [debounced, setDebounced] = useState(value);

	useEffect(() => {
		const timer = setTimeout(() => setDebounced(value), delay);
		return () => clearTimeout(timer);
	}, [value, delay]);

	return debounced;
}

const DEFAULT_LIMIT = 20;
const PAGE_SIZE_OPTIONS = [10, 20, 25, 50, 100];

const formatDate = (value) => {
	if (!value) return "—";
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

export default function PendingRequests() {
	const { id } = useParams();
	const { data, error, isLoading, mutate } = useGetPendingRequests(id);

	const apiBody = data?.body;
	const apiPage = apiBody?.page ?? 1;
	const apiLimit = apiBody?.limit ?? DEFAULT_LIMIT;

	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(DEFAULT_LIMIT);
	const [search, setSearch] = useState("");
	const debouncedSearch = useDebouncedValue(search);

	const requests = useMemo(() => {
		if (!apiBody) return [];
		if (Array.isArray(apiBody.pending_requests)) return apiBody.pending_requests;
		return [];
	}, [apiBody]);

	useEffect(() => {
		if (apiLimit == null) return;
		setLimit((prev) => (prev === DEFAULT_LIMIT ? apiLimit : prev));
	}, [apiLimit]);

	useEffect(() => {
		if (apiPage == null) return;
		setPage((prev) => (prev === 1 ? apiPage : prev));
	}, [apiPage]);

	useEffect(() => {
		setPage(1);
	}, [debouncedSearch, limit]);

	const filtered = useMemo(() => {
		if (!debouncedSearch) return requests;
		const term = debouncedSearch.toLowerCase();
		return requests.filter((req) => {
			const name = (req?.displayname || "").toLowerCase();
			const email = (req?.email || "").toLowerCase();
			return name.includes(term) || email.includes(term);
		});
	}, [requests, debouncedSearch]);

	const total = apiBody?.total ?? filtered.length;
	const totalPages = Math.max(1, Math.ceil(total / limit));

	useEffect(() => {
		setPage((prev) => Math.min(prev, totalPages));
	}, [totalPages]);

	const startIndex = (page - 1) * limit;
	const currentRows = filtered.slice(startIndex, startIndex + limit);

	const handleImageError = (event) => {
		event.target.onerror = null;
		event.target.src = fallbackAvatar;
	};

	const updateOptimistic = (memberId) => (current) => {
		if (!current?.body?.pending_requests) return current;
		const filteredRequests = current.body.pending_requests.filter(
			(req) => req.member_id !== memberId
		);
		return {
			...current,
			body: {
				...current.body,
				pending_requests: filteredRequests,
				total: Math.max(0, (current.body.total ?? filteredRequests.length)),
			},
		};
	};

	const handleDecision = async (memberId, action) => {
		const apiCall = action === "approve" ? postApprovePendingRequest : postRejectPendingRequest;
		const actionLabel = action === "approve" ? "approved" : "rejected";

		try {
			await mutate(
				async (current) => {
					await apiCall({ pageId: id, memberId });
					return current;
				},
				{
					optimisticData: updateOptimistic(memberId),
					rollbackOnError: true,
					populateCache: true,
					revalidate: true,
				}
			);
			toast.success(`Request ${actionLabel}.`);
		} catch (err) {
			console.error(`Failed to ${action} request`, err);
			toast.error(`Unable to ${action} request. Please try again.`);
		}
	};

	return (
		<div className="page-edit-manage-admins-container">
			<div>
				<h3 className="page-edit-manage-admins-title">Pending Membership Requests</h3>
				<p>Review and manage incoming requests to join this page.</p>
			</div>

			<div
				className="controls"
				style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}
			>
				<input
					type="search"
					placeholder="Search by name or email…"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					style={{ minWidth: 240 }}
					aria-label="Search pending requests"
				/>
				<label style={{ display: "flex", alignItems: "center", gap: 8 }}>
					Page size:
					<select
						value={limit}
						onChange={(e) => setLimit(Number(e.target.value))}
						aria-label="Pending requests page size"
					>
						{PAGE_SIZE_OPTIONS.map((option) => (
							<option key={option} value={option}>
								{option}
							</option>
						))}
					</select>
				</label>
				<div style={{ marginLeft: "auto", fontSize: 12, opacity: 0.8 }}>
					{isLoading ? "Loading…" : null}
				</div>
			</div>

			<div className="page-edit-manage-admins-content">
				{isLoading && requests.length === 0 ? (
					<div>Loading pending requests…</div>
				) : error ? (
					<div style={{ color: "crimson" }}>
						Failed to load pending requests: {String(error?.message || "Unknown error")}
					</div>
				) : total === 0 ? (
					<div>
						No pending requests{debouncedSearch ? ` for "${debouncedSearch}"` : ""}.
					</div>
				) : (
					<div style={{ padding: 0 }}>
						<table
							className="pending-requests-table"
							style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}
						>
							<thead>
								<tr>
									<th style={{ textAlign: "left", padding: "10px 16px", borderBottom: "1px solid rgba(0,0,0,0.1)" }}>
										Member
									</th>
									<th style={{ textAlign: "left", padding: "10px 16px", borderBottom: "1px solid rgba(0,0,0,0.1)" }}>
										Requested
									</th>
									<th style={{ textAlign: "right", padding: "10px 16px", borderBottom: "1px solid rgba(0,0,0,0.1)" }}>
										Actions
									</th>
								</tr>
							</thead>
							<tbody>
								{currentRows.map((request) => {
									const name = request.displayname || "Unknown user";
									const avatar = request.photo_url || fallbackAvatar;
									const profileHref = request.user_id ? `/profile/${request.user_id}` : null;
									const requestedAt = formatDate(request.request_date);

									return (
										<tr key={request?.member_id || request?.user_id || name} style={{ borderBottom: "1px solid rgba(0,0,0,0.05)" }}>
											<td style={{ padding: "14px 16px" }}>
												<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
													<img
														src={avatar}
														alt={`${name}'s avatar`}
														onError={handleImageError}
														style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover", boxShadow: "0 2px 6px rgba(0,0,0,0.08)" }}
													/>
													<div style={{ display: "flex", flexDirection: "column" }}>
														{profileHref ? <Link to={profileHref}>{name}</Link> : <span>{name}</span>}
														<span style={{ fontSize: 12, opacity: 0.65 }}>Member ID: {request.member_id}</span>
													</div>
												</div>
											</td>
											<td style={{ padding: "14px 16px", minWidth: 180 }}>
												{requestedAt}
											</td>
											<td style={{ padding: "14px 16px", textAlign: "right", minWidth: 200 }}>
												<div style={{ display: "inline-flex", gap: 8 }}>
													<button
														onClick={() => handleDecision(request.member_id, "approve")}
														style={{
															padding: "6px 14px",
															borderRadius: 6,
															border: "1px solid #3d7b45",
															background: "#4caf50",
															color: "#fff",
															cursor: "pointer",
														}}
													>
														Approve
													</button>
													<button
														onClick={() => handleDecision(request.member_id, "reject")}
														style={{
															padding: "6px 14px",
															borderRadius: 6,
															border: "1px solid #b53f3f",
															background: "#f44336",
															color: "#fff",
															cursor: "pointer",
														}}
													>
														Reject
													</button>
												</div>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</div>

			{total > 0 && (
				<div
					className="pagination"
					style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "center", marginTop: 16 }}
				>
					<button disabled={page === 1} onClick={() => setPage(1)}>
						« First
					</button>
					<button disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
						‹ Prev
					</button>
					<span>
						Page
						<input
							type="number"
							min={1}
							max={totalPages}
							value={page}
							onChange={(e) => {
								const value = Number(e.target.value);
								if (Number.isNaN(value)) return;
								setPage(Math.min(Math.max(1, value), totalPages));
							}}
							style={{ width: 60, margin: "0 6px", textAlign: "center" }}
							aria-label="Pending requests page"
						/>
						of {totalPages}
					</span>
					<button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
						Next ›
					</button>
					<button disabled={page >= totalPages} onClick={() => setPage(totalPages)}>
						Last »
					</button>
				</div>
			)}
		</div>
	);
}
